import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { frameAmounts, discountBadges, priceFormatter } from './artesta-frame-prices.js';

const ROOT_SELECTOR = '[data-collection-sets]';

function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
  if (isNaN(number) || number === null) return '0';
  const fixed = (number / 100).toFixed(precision);
  const [whole, fraction] = fixed.split('.');
  const withThousands = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  return precision > 0 ? `${withThousands}${decimal}${fraction}` : withThousands;
}

function formatMoney(cents, format) {
  const moneyFormat = format || '{{amount}}';
  const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = moneyFormat.match(placeholderRegex);
  if (!match) return `${cents}`;

  let formatted = '';
  switch (match[1]) {
    case 'amount':
      formatted = formatWithDelimiters(value, 2, ',', '.');
      break;
    case 'amount_no_decimals':
      formatted = formatWithDelimiters(value, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      formatted = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = formatWithDelimiters(value, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      formatted = formatWithDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      formatted = formatWithDelimiters(value, 2, ' ', '.');
      break;
    case 'amount_with_apostrophe_separator':
      formatted = formatWithDelimiters(value, 2, "'", '.');
      break;
    default:
      formatted = formatWithDelimiters(value, 2, ',', '.');
  }

  return moneyFormat.replace(placeholderRegex, formatted);
}

function initCarousel(root) {
  const track = root.querySelector('[data-carousel-track]');
  const prev = root.querySelector('[data-carousel-prev]');
  const next = root.querySelector('[data-carousel-next]');
  const counter = root.querySelector('[data-carousel-count]');
  if (!track) return;

  const getStep = () => {
    const card = track.querySelector('[data-set-item]');
    if (!card) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0');
    return card.getBoundingClientRect().width + gap;
  };

  const updateCounter = () => {
    if (!counter) return;
    const cards = Array.from(track.querySelectorAll('[data-set-item]'));
    if (!cards.length) return;

    const firstCard = cards[0];
    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0');
    const step = cardWidth + gap;
    const index = Math.min(cards.length, Math.max(1, Math.round(track.scrollLeft / step) + 1));
    counter.textContent = `${index}/${cards.length}`;
  };

  prev?.addEventListener('click', () => {
    track.scrollBy({ left: -getStep(), behavior: 'smooth' });
  });

  next?.addEventListener('click', () => {
    track.scrollBy({ left: getStep(), behavior: 'smooth' });
  });

  track.addEventListener('scroll', () => {
    window.requestAnimationFrame(updateCounter);
  });

  updateCounter();
}

function initSets(root) {
  if (root.dataset.setsReady === 'true') return;
  root.dataset.setsReady = 'true';

  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  const format = priceFormatter(root.dataset.locale || document.documentElement.lang || 'en', root.dataset.currency || 'EUR');
  const money = cents => format.format(cents / 100);
  const addButton = root.querySelector('[data-set-add]');
  const addLabel = addButton?.dataset.label || '';
  const availabilityMessage = root.querySelector('[data-sets-availability]');
  const items = [];

  root.querySelectorAll('[data-set-item]').forEach((item) => {
    const dataEl = item.querySelector('[data-product-json]');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent || '{}');
    } catch (err) {
      return;
    }

    const media = item.querySelector('[data-media]');
    const priceEl = item.querySelector('[data-price]');
    const compareEl = item.querySelector('[data-compare-price]');
    const componentBadges = item.querySelector('[data-set-component-discounts]');
    const optionInputs = Array.from(item.querySelectorAll('[data-option-input]'));
    const toggle = item.querySelector('[data-toggle]');
    const personalize = item.querySelector('[data-personalize]');
    const availabilityEl = item.querySelector('[data-availability]');
    const defaultVariantId = Number(item.dataset.defaultVariantId || data.defaultVariantId);

    let currentVariant = data.variants.find((variant) => Number(variant.id) === defaultVariantId);
    if (!currentVariant) currentVariant = data.variants[0];

    const state = {
      el: item,
      data,
      media,
      priceEl,
      compareEl,
      optionInputs,
      toggle,
      availabilityEl,
      included: true,
      available: true,
      currentVariant,
    };

    const getSelectedOptions = () => {
      const options = [];
      optionInputs.forEach((input) => {
        if (!input.checked) return;
        const index = Number(input.dataset.optionIndex || 0);
        options[index] = input.value;
      });
      return options;
    };

    const updateLegends = () => {
      const selected = getSelectedOptions();
      item.querySelectorAll('[data-legend-selected-value]').forEach((el) => {
        const fieldset = el.closest('[data-option-index]');
        if (!fieldset) return;
        const index = Number(fieldset.dataset.optionIndex || 0);
        if (selected[index]) el.textContent = selected[index];
      });
    };

    const findVariant = () => {
      const selected = getSelectedOptions();
      return data.variants.find((variant) => {
        return (
          (!selected[0] || variant.option1 === selected[0]) &&
          (!selected[1] || variant.option2 === selected[1]) &&
          (!selected[2] || variant.option3 === selected[2])
        );
      });
    };

    const normalizeValues = (values = []) =>
      values.map((val) => {
        if (typeof val === 'string') return val;
        return val?.value || val?.name || val?.title || '';
      });

    const updateMediaClass = (variant, selectedOptions = []) => {
      if (!media || !data.options?.length) return;
      const option1Values = normalizeValues(data.options[0]?.values || []);
      const option2Values = normalizeValues(data.options[1]?.values || []);
      const value1 = selectedOptions[0] || variant?.option1;
      const value2 = selectedOptions[1] || variant?.option2;
      let idx1 = option1Values.indexOf(value1);
      let idx2 = option2Values.indexOf(value2);
      if (idx1 < 0) idx1 = 0;
      if (idx2 < 0) idx2 = 0;
      media.classList.forEach((cls) => {
        if (cls.startsWith('option-')) media.classList.remove(cls);
      });
      media.classList.add(`option-${idx1}-${idx2}`);
    };

    const updatePrice = (variant) => {
      const price = variant && variant.price !== null && variant.price !== '' ? Number(variant.price) : NaN;
      const valid = Number.isSafeInteger(price) && price >= 0;
      const compare = Number(variant?.compare_at_price);
      const onSale = valid && Number.isSafeInteger(compare) && compare > price;
      if (priceEl) priceEl.textContent = valid ? money(price) : '';
      if (compareEl) {
        compareEl.textContent = onSale ? money(compare) : '';
        compareEl.classList.toggle('hidden', !onSale);
      }
      if (componentBadges) {
        const selected = variant?.options || [variant?.option1, variant?.option2, variant?.option3];
        const amounts = valid ? frameAmounts(data.variants, selected) : null;
        const percentages = amounts ? discountBadges(amounts) : {base: null, frame: null, total: null};
        for (const kind of ['base', 'frame', 'total']) {
          const badge = componentBadges.querySelector(`[data-set-discount-${kind}]`);
          if (!badge) continue;
          const percent = percentages[kind];
          badge.hidden = !percent;
          const label = kind === 'frame' ? data.options?.[1]?.name : variant?.option1;
          if (percent) badge.textContent = `${label || ''} -${percent}%`;
        }
      }
    };

    const updateAvailability = (variant) => {
      const isAvailable = !!variant && variant.available;
      state.available = isAvailable;
      const show = state.included && !isAvailable;
      state.el.classList.toggle('is-unavailable', show);
      if (availabilityEl) {
        availabilityEl.hidden = !show;
        if (show) {
          availabilityEl.textContent =
            availabilityEl.dataset.unavailableText || 'No disponible';
        }
      }
    };

    const updateVariant = (variant) => {
      const selected = getSelectedOptions();
      state.currentVariant = variant || null;
      state.el.dataset.variantId = variant ? variant.id : '';
      updateMediaClass(variant, selected);
      updatePrice(variant);
      updateAvailability(variant);
      updateLegends();
      updateTotal();
    };

    const updateToggle = () => {
      state.el.classList.toggle('is-excluded', !state.included);
      if (toggle) {
        toggle.setAttribute('aria-pressed', state.included ? 'true' : 'false');
        toggle.textContent = state.included ? toggle.dataset.textRemove : toggle.dataset.textAdd;
      }
      updateAvailability(state.currentVariant);
      updateTotal();
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const variant = findVariant();
        updateVariant(variant || null);
      });
    });

    toggle?.addEventListener('click', () => {
      state.included = !state.included;
      updateToggle();
    });

    personalize?.addEventListener('click', () => {
      const isOpen = state.el.classList.toggle('is-options-open');
      personalize.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      const openLabel = personalize.dataset.textOpen || 'Personalizar';
      const closeLabel = personalize.dataset.textClose || 'Cerrar opciones';
      personalize.textContent = isOpen ? closeLabel : openLabel;
    });

    updateVariant(currentVariant);
    updateToggle();

    items.push(state);
  });

  function updateTotal() {
    if (!addButton) return;
    let total = 0;
    let reference = 0;
    let included = 0;
    let unknownPrice = false;
    let hasUnavailable = false;

    items.forEach((item) => {
      if (!item.included) return;
      if (!item.available) hasUnavailable = true;
      included += 1;
      const price = item.currentVariant && item.currentVariant.price !== null && item.currentVariant.price !== '' ? Number(item.currentVariant.price) : NaN;
      if (!Number.isSafeInteger(price) || price < 0) {
        unknownPrice = true;
        hasUnavailable = true;
        return;
      }
      const compare = Number(item.currentVariant?.compare_at_price);
      total += price;
      reference += Number.isSafeInteger(compare) && compare > price ? compare : price;
    });

    const summary = root.querySelector('[data-set-price-summary]');
    if (summary) {
      summary.hidden = included === 0 || unknownPrice;
      const current = summary.querySelector('[data-set-total]');
      const before = summary.querySelector('[data-set-compare]');
      if (current) current.textContent = money(total);
      if (before) {
        before.hidden = reference <= total;
        before.textContent = reference > total ? money(reference) : '';
      }

    }
    const label = addButton.querySelector('[data-set-add-label]');
    if (label) label.textContent = addLabel;
    else addButton.textContent = unknownPrice ? addLabel : `${addLabel} ${money(total)}`;
    addButton.hidden = included === 0;
    addButton.disabled = total === 0 || hasUnavailable || root.dataset.priceDemo === 'true';

    if (availabilityMessage) {
      availabilityMessage.hidden = !hasUnavailable;
      if (hasUnavailable) {
        availabilityMessage.textContent =
          availabilityMessage.dataset.unavailableText || 'Hay productos no disponibles.';
      }
    }
  }

  async function addSetToCart() {
    if (!addButton || addButton.disabled || root.dataset.priceDemo === 'true') return;
    const payload = items
      .filter((item) => item.included && item.available)
      .map((item) => ({ id: item.currentVariant?.id, quantity: 1 }))
      .filter((item) => item.id);

    if (!payload.length) return;

    addButton.disabled = true;

    const formData = new FormData();
    payload.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = [];
    cartItemsComponents.forEach((component) => {
      if (component.dataset.sectionId) sections.push(component.dataset.sectionId);
    });
    if (sections.length) formData.append('sections', sections.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (data.status) {
        root.dispatchEvent(
          new CartErrorEvent(root.id || '', data.message, data.description, data.errors)
        );
        return;
      }

      root.dispatchEvent(
        new CartAddEvent({}, root.id || '', {
          source: 'collection-sets',
          itemCount: payload.length,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      addButton.disabled = false;
      updateTotal();
    }
  }

  addButton?.addEventListener('click', addSetToCart);
  updateTotal();
  initCarousel(root);
}

function initAll() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => initSets(root));
}

document.addEventListener('DOMContentLoaded', initAll);
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target?.querySelector?.(ROOT_SELECTOR);
  if (root) initSets(root);
});